import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Minus, Loader2, RotateCcw, User, Car, Building2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const AddEditCustomer = ({ isOpen, onClose, customer, selectedBranchId, branches, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', carPlate: '', carDetails: '', bonusBalance: 0, primaryBranchId: '',
  });
  const [bonusToAdd, setBonusToAdd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [archivedRecord, setArchivedRecord] = useState(null);
  const initRef = useRef(null);

  // Initialize form ONLY on open or customer change
  useEffect(() => {
    if (!isOpen) { initRef.current = null; return; }
    const key = customer ? customer.id : '__create__';
    if (initRef.current === key) return;
    initRef.current = key;

    if (customer) {
      setFormData({
        name: customer.name || '', email: customer.email || '', phone: customer.phone || '',
        carPlate: customer.carPlate || '', carDetails: customer.carDetails || '',
        bonusBalance: customer.bonusBalance || 0,
        primaryBranchId: customer.primaryBranchId || '',
      });
    } else {
      setFormData({
        name: '', email: '', phone: '', carPlate: '', carDetails: '', bonusBalance: 0,
        primaryBranchId: (selectedBranchId && selectedBranchId !== 'all') ? selectedBranchId : '',
      });
    }
    setError('');
    setArchivedRecord(null);
  }, [isOpen, customer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setArchivedRecord(null);
  };

  const handleRestore = async (archivedId) => {
    setLoading(true);
    setError('');
    try {
      const token = getFromLocalStorage('accessToken');
      const res = await fetch(`${BACKEND_URL}/api/customers/${archivedId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Geri yukleme basarisiz');
      }
      toast({ title: "Musteri Geri Yuklendi", description: "Arsivlenmis musteri basariyla geri yuklendi." });
      setArchivedRecord(null);
      if (onSave) onSave();
      onClose();
    } catch (err) {
      setError(err.message || 'Geri yukleme basarisiz');
      toast({ title: "Hata", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const parseErrorDetail = (detail) => {
    if (typeof detail === 'object' && detail?.code === 'ARCHIVED_EMAIL') return detail;
    if (typeof detail === 'string') return { message: detail };
    return { message: JSON.stringify(detail) };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Ad Soyad zorunludur.');
      return;
    }

    setLoading(true);
    setError('');
    setArchivedRecord(null);

    try {
      const token = getFromLocalStorage('accessToken');
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

      if (customer) {
        // UPDATE existing customer
        const updateData = { name: formData.name };
        if (formData.phone) updateData.phone = formData.phone;
        if (formData.carPlate) updateData.carPlate = formData.carPlate;
        if (formData.carDetails) updateData.carDetails = formData.carDetails;
        updateData.bonusBalance = parseFloat(formData.bonusBalance) || 0;
        if (formData.email && formData.email !== (customer.email || '')) {
          updateData.email = formData.email;
        }
        // Branch update
        if (formData.primaryBranchId) {
          updateData.primaryBranchId = formData.primaryBranchId;
        }

        const res = await fetch(`${BACKEND_URL}/api/customers/${customer.id}`, {
          method: 'PUT', headers, body: JSON.stringify(updateData),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const parsed = parseErrorDetail(err.detail);
          if (parsed.code === 'ARCHIVED_EMAIL') {
            setArchivedRecord(parsed);
            setError(parsed.message);
          } else {
            throw new Error(parsed.message || `Guncelleme basarisiz (${res.status})`);
          }
          return;
        }
        toast({ title: "Musteri Guncellendi", description: `${formData.name} basariyla guncellendi.` });
      } else {
        // CREATE new customer
        const createData = { name: formData.name };
        if (formData.email) createData.email = formData.email;
        if (formData.phone) createData.phone = formData.phone;
        if (formData.carPlate) createData.carPlate = formData.carPlate;
        if (formData.carDetails) createData.carDetails = formData.carDetails;
        if (formData.primaryBranchId) {
          createData.primaryBranchId = formData.primaryBranchId;
        }

        const res = await fetch(`${BACKEND_URL}/api/customers/register`, {
          method: 'POST', headers, body: JSON.stringify(createData),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const parsed = parseErrorDetail(err.detail);
          if (parsed.code === 'ARCHIVED_EMAIL') {
            setArchivedRecord(parsed);
            setError(parsed.message);
          } else {
            throw new Error(parsed.message || `Kayit basarisiz (${res.status})`);
          }
          return;
        }
        const result = await res.json();
        if (result.vehicleWarning) {
          toast({ title: "Uyari", description: result.vehicleWarning, variant: "destructive" });
        }
        const emailSent = result.activation_email_sent;
        if (formData.email && emailSent) {
          toast({ title: "Musteri Olusturuldu", description: `${formData.name} kaydedildi. Aktivasyon emaili gonderildi.` });
        } else if (formData.email && !emailSent) {
          toast({ title: "Musteri Olusturuldu", description: `${formData.name} kaydedildi. Aktivasyon emaili gonderilemedi.` });
        } else {
          toast({ title: "Musteri Olusturuldu", description: `${formData.name} basariyla kaydedildi.` });
        }
      }

      if (onSave) onSave();
      onClose();
    } catch (err) {
      const msg = err.message || 'Bilinmeyen hata';
      setError(msg);
      toast({ title: "Hata", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const activeBranches = (branches || []).filter(b => !b.isDeleted);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !loading) onClose(); }}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white text-2xl font-display tracking-tight">
            {customer ? 'Musteriyi Duzenle' : 'Yeni Musteri Ekle'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {customer ? 'Musteri bilgilerini guncelleyin' : 'Sadece Ad Soyad yeterli. Diger bilgiler sonra eklenebilir.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-400 text-sm" data-testid="customer-form-error">
              {error}
              {archivedRecord && (
                <Button type="button" size="sm" onClick={() => handleRestore(archivedRecord.archivedId)}
                  disabled={loading}
                  className="mt-2 w-full bg-yellow-600 hover:bg-yellow-700 text-white text-xs"
                  data-testid="customer-restore-btn">
                  <RotateCcw className="h-3 w-3 mr-1" />
                  {archivedRecord.archivedName ? `"${archivedRecord.archivedName}" Geri Yukle` : 'Arsivlenen Kaydi Geri Yukle'}
                </Button>
              )}
            </div>
          )}

          {/* ===== SECTION 1: CUSTOMER INFO ===== */}
          <div className="border border-blue-700/40 rounded-lg p-4 space-y-3 bg-blue-900/10">
            <div className="flex items-center gap-2 mb-1">
              <User className="h-4 w-4 text-blue-400" />
              <span className="text-blue-400 font-semibold text-sm">Musteri Bilgileri</span>
            </div>
            <div>
              <Label className="text-white">Ad Soyad *</Label>
              <Input name="name" value={formData.name} onChange={handleChange}
                placeholder="Musteri adi ve soyadi"
                className="bg-gray-900 border-gray-700 text-white mt-1" required data-testid="customer-name-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-sm">Email <span className="text-gray-600">(opsiyonel)</span></Label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange}
                  placeholder="ornek@email.com"
                  className="bg-gray-900 border-gray-700 text-white mt-1" data-testid="customer-email-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Telefon <span className="text-gray-600">(opsiyonel)</span></Label>
                <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="0532 123 4567"
                  className="bg-gray-900 border-gray-700 text-white mt-1" data-testid="customer-phone-input" />
              </div>
            </div>
          </div>

          {/* ===== SECTION: BRANCH ===== */}
          {activeBranches.length > 0 && (
            <div className="border border-indigo-700/40 rounded-lg p-4 space-y-3 bg-indigo-900/10">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="h-4 w-4 text-indigo-400" />
                <span className="text-indigo-400 font-semibold text-sm">Sube</span>
              </div>
              <select name="primaryBranchId" value={formData.primaryBranchId} onChange={handleChange}
                className="w-full bg-gray-900 border border-gray-700 text-white rounded-md p-2 text-sm"
                data-testid="customer-branch-select">
                <option value="">Sube sec...</option>
                {activeBranches.map(b => (
                  <option key={b.id} value={b.id}>{b.name} {b.code ? `(${b.code})` : ''}</option>
                ))}
              </select>
            </div>
          )}

          {/* ===== SECTION 2: VEHICLE INFO ===== */}
          <div className="border border-brand/40 rounded-lg p-4 space-y-3 bg-brand/10">
            <div className="flex items-center gap-2 mb-1">
              <Car className="h-4 w-4 text-brand" />
              <span className="text-brand font-semibold text-sm">Arac Bilgileri</span>
              <span className="text-gray-500 text-xs ml-1">(opsiyonel)</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-sm">Arac Plakasi</Label>
                <Input name="carPlate" value={formData.carPlate} onChange={handleChange} placeholder="34 ABC 123"
                  className="bg-gray-900 border-gray-700 text-white mt-1" data-testid="customer-plate-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Arac Detaylari</Label>
                <Input name="carDetails" value={formData.carDetails} onChange={handleChange}
                  placeholder="2020 BMW 3 Serisi Siyah" className="bg-gray-900 border-gray-700 text-white mt-1" data-testid="customer-car-details-input" />
              </div>
            </div>
            {!customer && formData.carPlate && (
              <p className="text-gray-500 text-xs">
                Musteri olusturulduktan sonra bu arac otomatik olarak musteriye baglanacaktir.
              </p>
            )}
          </div>

          {/* ===== SECTION 3: BONUS (edit only) ===== */}
          {customer && (
            <div className="border border-yellow-700/40 rounded-lg p-4 space-y-3 bg-yellow-900/10">
              <Label className="text-yellow-400 font-semibold text-sm">Bonus Yonetimi</Label>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Mevcut Bonus</p>
                  <p className="text-yellow-500 font-semibold text-2xl">{formData.bonusBalance} TL</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" value={bonusToAdd} onChange={(e) => setBonusToAdd(e.target.value)}
                  placeholder="Tutar girin" className="bg-gray-800 border-gray-600 text-white flex-1" />
                <Button type="button" onClick={() => {
                  const amount = parseFloat(bonusToAdd);
                  if (amount > 0) {
                    setFormData(prev => ({ ...prev, bonusBalance: prev.bonusBalance + amount }));
                    setBonusToAdd('');
                  }
                }} className="bg-green-600 hover:bg-green-700 text-white"><Plus className="h-4 w-4 mr-1" />Ekle</Button>
                <Button type="button" onClick={() => {
                  const amount = parseFloat(bonusToAdd);
                  if (amount > 0 && formData.bonusBalance >= amount) {
                    setFormData(prev => ({ ...prev, bonusBalance: prev.bonusBalance - amount }));
                    setBonusToAdd('');
                  }
                }} className="bg-red-600 hover:bg-red-700 text-white"><Minus className="h-4 w-4 mr-1" />Dus</Button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700">Iptal</Button>
            <Button type="submit" disabled={loading} data-testid="customer-submit-btn"
              className="flex-1 bg-gradient-to-r from-brand to-brand hover:from-brand hover:to-brand/90 text-white">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Kaydediliyor...</> : (customer ? 'Guncelle' : 'Kaydet')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditCustomer;
